/**
 * Guest-side input-to-photon correlation.
 *
 * Owns the lifecycle of one measurement: decide whether to sample an input, remember when
 * it happened, accept the host's frame tag, and close the loop when that frame is actually
 * presented. See `inputPhotonContracts.ts` for why this needs no clock synchronisation.
 *
 * Deliberately pure — no timers, no DOM, no transport. The caller supplies wall time and
 * feeds it events, which is what makes the correlation testable without a live session.
 */

import {
  INPUT_PHOTON_MAX_AGE_MS,
  INPUT_PHOTON_SAMPLE_INTERVAL_MS,
  INPUT_PHOTON_WINDOW_SAMPLES,
  isPlausibleInputPhotonLatency,
  type InputPhotonChainCounts,
  type InputPhotonSample,
  type InputPhotonSummary,
  type InputPhotonTag,
} from './inputPhotonContracts';

export type InputPhotonTrackerOptions = {
  /** Minimum gap between sampled inputs, ms. */
  sampleIntervalMs?: number;
  /** Rolling window size for the percentile summary. */
  windowSamples?: number;
  /** How long a pending input waits for its frame tag before expiring, ms. */
  maxPendingAgeMs?: number;
  /** Invoked for every completed measurement, for logging / health events. */
  onSample?: (sample: InputPhotonSample) => void;
};

export class InputPhotonTracker {
  private readonly sampleIntervalMs: number;
  private readonly windowSamples: number;
  private readonly maxPendingAgeMs: number;
  private readonly onSample?: (sample: InputPhotonSample) => void;

  /** inputSeq → guest wall time of the originating DOM event. */
  private readonly pendingInputs = new Map<number, number>();
  /** rtpTimestamp → inputSeq, from host tags awaiting their frame. */
  private readonly taggedFrames = new Map<number, number>();
  private readonly recentLatencies: number[] = [];

  private seq = 0;
  private lastSampledAtMs = Number.NEGATIVE_INFINITY;
  private lastMs: number | null = null;
  private readonly chain: InputPhotonChainCounts = { sampled: 0, tagsAccepted: 0, tagsIgnored: 0, framesSeen: 0, implausible: 0 };

  constructor(options: InputPhotonTrackerOptions = {}) {
    this.sampleIntervalMs = options.sampleIntervalMs ?? INPUT_PHOTON_SAMPLE_INTERVAL_MS;
    this.windowSamples = options.windowSamples ?? INPUT_PHOTON_WINDOW_SAMPLES;
    this.maxPendingAgeMs = options.maxPendingAgeMs ?? INPUT_PHOTON_MAX_AGE_MS;
    if (options.onSample) this.onSample = options.onSample;
  }

  /**
   * Claim a sequence number for an input that is about to be sent, or null when this input
   * falls inside the sampling interval and should go out untagged.
   *
   * Returning null rather than sampling everything is the point: input can fire every
   * frame, and the measurement is only useful as a trend.
   */
  sampleInput(nowMs: number): number | null {
    if (nowMs - this.lastSampledAtMs < this.sampleIntervalMs) return null;
    this.lastSampledAtMs = nowMs;
    this.seq += 1;
    this.pendingInputs.set(this.seq, nowMs);
    this.chain.sampled += 1;
    this.expirePending(nowMs);
    return this.seq;
  }

  /** Accept the host's "input N is answered by frame R" tag. */
  acceptTag(tag: InputPhotonTag): void {
    // Only tags for inputs we are still waiting on: anything else is a replay or a tag for
    // an input that already expired, and correlating it would invent a measurement.
    if (!this.pendingInputs.has(tag.inputSeq)) {
      this.chain.tagsIgnored += 1;
      return;
    }
    this.chain.tagsAccepted += 1;
    this.taggedFrames.set(tag.rtpTimestamp, tag.inputSeq);
  }

  /**
   * Report a frame reaching the screen. Returns the completed measurement when this frame
   * was the one a sampled input was waiting for, otherwise null.
   */
  onFramePresented(rtpTimestamp: number, presentedWallMs: number): InputPhotonSample | null {
    this.chain.framesSeen += 1;
    const inputSeq = this.taggedFrames.get(rtpTimestamp);
    if (inputSeq === undefined) return null;
    this.taggedFrames.delete(rtpTimestamp);

    const inputWallMs = this.pendingInputs.get(inputSeq);
    if (inputWallMs === undefined) return null;
    this.pendingInputs.delete(inputSeq);

    const latencyMs = presentedWallMs - inputWallMs;
    if (!isPlausibleInputPhotonLatency(latencyMs)) {
      this.chain.implausible += 1;
      return null;
    }

    this.lastMs = latencyMs;
    this.recentLatencies.push(latencyMs);
    if (this.recentLatencies.length > this.windowSamples) this.recentLatencies.shift();

    const sample: InputPhotonSample = { inputSeq, rtpTimestamp, latencyMs };
    this.onSample?.(sample);
    return sample;
  }

  summary(): InputPhotonSummary {
    const chain = { ...this.chain };
    if (this.recentLatencies.length === 0) {
      return { lastMs: null, p50Ms: null, p95Ms: null, sampleCount: 0, chain };
    }
    const sorted = [...this.recentLatencies].sort((a, b) => a - b);
    return {
      lastMs: this.lastMs,
      p50Ms: sorted[Math.floor((sorted.length - 1) * 0.5)] ?? null,
      p95Ms: sorted[Math.floor((sorted.length - 1) * 0.95)] ?? null,
      sampleCount: sorted.length,
      chain,
    };
  }

  /**
   * Drop correlation state that belongs to the previous connection.
   *
   * RTP timestamps restart with a new SSRC, so a tag held across a reconnect can collide
   * with a frame from the new stream and produce a reading built from two different
   * sessions. The measured history survives — it describes this guest, not this socket.
   */
  resetForNewConnection(): void {
    this.pendingInputs.clear();
    this.taggedFrames.clear();
  }

  private expirePending(nowMs: number): void {
    for (const [inputSeq, atMs] of this.pendingInputs) {
      if (nowMs - atMs <= this.maxPendingAgeMs) continue;
      this.pendingInputs.delete(inputSeq);
      for (const [rtp, seq] of this.taggedFrames) {
        if (seq === inputSeq) this.taggedFrames.delete(rtp);
      }
    }
  }
}
